import React, { forwardRef, type Ref } from 'react';

import { InputAdornment, TextField as MuiTextField, TextFieldProps as MuiTextFieldProps } from '@mui/material';
import { VISUAL_REFRESH } from 'common/utils';
import { TextInput as DsTextInput, TextInputPill as DsTextInputPill } from 'components';

import { FormFieldHint } from '../FormFieldHint';
import { toDsProps } from './toDsProps';
import { calculateShrink } from './utils';

export type TextFieldProps = Pick<
  MuiTextFieldProps,
  | 'autoComplete'
  | 'autoFocus'
  | 'defaultValue'
  | 'disabled'
  | 'error'
  | 'fullWidth'
  | 'inputProps'
  | 'InputProps'
  | 'label'
  | 'multiline'
  | 'placeholder'
  | 'required'
  | 'rows'
  | 'maxRows'
  | 'size'
  | 'value'
  | 'onBlur'
  | 'onChange'
  | 'onClick'
  | 'onKeyDown'
  | 'type'
  | 'sx'
  | 'variant'
  | 'onFocus'
> & {
  hint?: string;
  wrapHint?: boolean;
  prefix?: string | React.ReactElement;
  shrinkLabel?: boolean;
  suffix?: string | React.ReactElement;
  /**
   * Render the Design System 2.0 **pill** shape rather than the standard 60px input.
   *
   * Refresh-only, and inert while the flag is off: MUI draws no pill, so this cannot be mapped
   * onto the legacy element. It exists because the pill is a separate Figma component set rather
   * than a variant, so which shape a field takes is a call-site decision that `toDsProps` has no
   * way to infer.
   *
   * TODO(HNS-264): folds away with the flag, at which point call sites pick the DS component
   * directly instead of asking this wrapper for a shape.
   */
  pill?: boolean;
};

export const TextField = forwardRef<HTMLDivElement, TextFieldProps>(
  ({ error, disabled, hint, prefix, suffix, shrinkLabel, wrapHint = true, pill, ...restOfProps }, ref) => {
    /**
     * The visual-refresh gate lives HERE, not at the call sites — the 73-odd files that reach this
     * component (most through `ReactHookForm/TextField`) keep its MUI-shaped API and never mention
     * the flag. `toDsProps` returns `null` for anything DS 2.0 cannot express, which falls through
     * to the MUI implementation below.
     */
    const dsProps = VISUAL_REFRESH
      ? toDsProps({ error, hint, prefix, suffix, shrinkLabel, wrapHint, ...restOfProps })
      : null;

    if (dsProps) {
      /**
       * Consumed or refused by `toDsProps`, and meaningless on a component with no Emotion and no
       * MUI slots — so they must not be forwarded. Everything else (`name`, `value`, `onChange`,
       * `onBlur`, `type`, `placeholder`, `required`, `autoComplete`, `autoFocus`) passes straight
       * through, which is what keeps the react-hook-form wiring intact.
       */
      const {
        InputProps: _InputProps,
        inputProps: _inputProps,
        fullWidth: _fullWidth,
        label: _label,
        maxRows: _maxRows,
        multiline: _multiline,
        rows: _rows,
        size: _size,
        sx: _sx,
        variant: _variant,
        // Narrowed and re-supplied by `toDsProps`, which is the only place that can prove MUI's
        // `unknown` is something an `<input>` will accept.
        value: _value,
        defaultValue: _defaultValue,
        ...rest
      } = restOfProps;

      /**
       * The legacy wrapper types its ref as `HTMLDivElement` but hands it to MUI as `inputRef`, so
       * it has always pointed at the input. `DsTextInput` forwards to its `Field.Control`, which is
       * that same input — the cast corrects the declared element type rather than changing where
       * the ref lands. `Ref` is invariant in its element, hence the double cast.
       */
      const dsRef = ref as unknown as Ref<HTMLInputElement>;

      /**
       * `TextInputPill` takes no `position` and draws no floating label — it shows the placeholder
       * and hides the label for assistive technology, falling back to the label as the placeholder
       * when none is given. Both shapes accept the same mapped props otherwise.
       */
      return pill ? (
        <DsTextInputPill {...rest} {...dsProps} disabled={disabled} ref={dsRef} />
      ) : (
        <DsTextInput {...rest} {...dsProps} disabled={disabled} ref={dsRef} />
      );
    }

    return (
      <MuiTextField
        inputRef={ref}
        variant="outlined"
        disabled={disabled}
        error={error}
        InputLabelProps={{
          shrink: calculateShrink({ shrinkLabel, defaultValue: restOfProps.defaultValue, value: restOfProps.value }),
        }}
        helperText={
          hint && (
            <FormFieldHint error={!disabled && !!error} noWrap={!wrapHint}>
              {hint}
            </FormFieldHint>
          )
        }
        {...restOfProps}
        InputProps={{
          ...restOfProps.InputProps,
          startAdornment: prefix && (
            <InputAdornment position="start" sx={{ paddingLeft: 1 }}>
              {prefix}
            </InputAdornment>
          ),
          endAdornment: suffix ? (
            <InputAdornment position="end">{suffix}</InputAdornment>
          ) : (
            restOfProps.InputProps?.endAdornment
          ),
        }}
      />
    );
  }
);
